// frontend/src/pages/ForgotPassword.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sessionService, sessionApi } from '../services/SessionApiService';
import { apiErrorFrom } from '../utils/parseApiError';
import { translateRequestError } from '../queries/api';
import { ICON, EnvelopeSimple } from '../icons';
import '../components/login/Login.css';

const STATE = {
  FORM: 'form',
  SUBMITTING: 'submitting',
  SENT: 'sent',
};

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [state, setState] = useState(STATE.FORM);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [csrfReady, setCsrfReady] = useState(false);
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');

  const logoSrc = '/images/manexys-logo.png';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const init = async () => {
      try {
        await sessionService.initCSRF();
        setCsrfReady(true);
      } catch (err) {
        console.error('Forgot password: CSRF init failed:', err.message);
        setError(translateRequestError(err, t, 'forgotPassword.errors.failed'));
        setCsrfReady(true);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setError('');
    setState(STATE.SUBMITTING);

    try {
      const response = await sessionApi.post('/auth/password-reset/request/', {
        email: email.trim(),
      });
      if (!response.ok) throw await apiErrorFrom(response, 'POST /auth/password-reset/request/');
      setState(STATE.SENT);
    } catch (err) {
      console.error('Forgot password request failed:', err.message);
      // The two refusals the endpoint makes carry no code: a 400 with
      // `errors` is the email Django would not accept, a 429 the IP limit
      // (authentication/views/auth.py). The server's message is dev-facing.
      if (err.status === 400 && err.body?.errors) setError(t('forgotPassword.errors.invalidEmail'));
      else if (err.status === 429) setError(t('apiErrors.rate_limit_exceeded'));
      else setError(translateRequestError(err, t, 'forgotPassword.errors.failed'));
      setState(STATE.FORM);
    }
  };

  const isFormDisabled = state === STATE.SUBMITTING;

  return (
    <div className="login-container">

      {/* Left: brand panel */}
      <div className="login-brand">
        <div className="login-brand-content">
          <img src={logoSrc} alt="Manexys" className="login-logo" />
          <div className="login-brand-accent" />
          <p className="login-tagline">Forecast with Precision.<br />Execute with Confidence.</p>
          <ul className="login-features">
            <li>AI-powered demand forecasting</li>
            <li>Smart inventory optimization</li>
            <li>Automated purchase orders</li>
          </ul>
        </div>
        <div className="login-brand-footer">
          <span>manexys.com</span>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="login-panel">
        <div className="login-form-wrap">

          {/* SENT STATE */}
          {state === STATE.SENT && (
            <div className="setup-success">
              <div className="setup-success-icon">
                <EnvelopeSimple size={ICON.display} />
              </div>
              <h1 className="login-title" style={{ textAlign: 'center' }}>
                {t('forgotPassword.sent.title')}
              </h1>
              <p className="login-subtitle" style={{ textAlign: 'center' }}>
                {t('forgotPassword.sent.message')}
              </p>
              <button
                className="login-button"
                onClick={() => navigate('/login')}
              >
                {t('forgotPassword.backToLogin')}
              </button>
            </div>
          )}

          {/* FORM STATE */}
          {(state === STATE.FORM || state === STATE.SUBMITTING) && (
            <>
              <h1 className="login-title">{t('forgotPassword.title')}</h1>
              <p className="login-subtitle">{t('forgotPassword.subtitle')}</p>

              {error && (
                <div className="login-info login-info-error">{error}</div>
              )}

              <form className="login-form" onSubmit={handleSubmit}>
                <div className="login-input-group">
                  <input
                    type="email"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="login-input"
                    disabled={isFormDisabled}
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  className="login-button"
                  aria-busy={isFormDisabled}
                  disabled={!email.trim() || isFormDisabled || !csrfReady}
                >
                  {t('forgotPassword.sendResetLink')}
                </button>
              </form>

              <button
                type="button"
                className="login-link-button"
                onClick={() => navigate('/login')}
              >
                {t('forgotPassword.backToLogin')}
              </button>
            </>
          )}

        </div>
      </div>

    </div>
  );
}
